import React, { useState, useEffect, useRef } from 'react';
import './Hero.css';

const slides = [
  {
    tag: 'Fleet Management System',
    headline: 'Know Where Your',
    accent: 'Fleet Is, Always.',
    subtext: "Real-time GPS tracking, intelligent alerts, and powerful analytics — engineered for Pakistan's most demanding fleets.",
    cta: 'Start Tracking Today',
    ctaSecondary: 'Watch Demo',
    stats: [
      { val: '6500+', lbl: 'Vehicles Tracked' },
      { val: '99.9%', lbl: 'Uptime' },
      { val: '24/7',  lbl: 'Support' },
    ],
  },
  {
    tag: 'Consumer Solutions',
    headline: 'Tracking At Your',
    accent: 'Fingertips, 24/7.',
    subtext: 'Never lose sight of your vehicle. Get instant location updates, geo-fence alerts, and complete trip history on your phone.',
    cta: 'Explore Solutions',
    ctaSecondary: 'View Packages',
    stats: [
      { val: '99.9%', lbl: 'Uptime' },
      { val: '1-min', lbl: 'Updates' },
      { val: '5★',    lbl: 'Rated' },
    ],
  },
  {
    tag: 'Waste Management',
    headline: 'Smarter Routes,',
    accent: 'Lower Costs.',
    subtext: 'Optimize waste collection routes, reduce fuel consumption, and ensure compliance — all from one powerful dashboard.',
    cta: 'Get Custom Quote',
    ctaSecondary: 'Learn More',
    stats: [
      { val: '30%',  lbl: 'Fuel Savings' },
      { val: '40%',  lbl: 'Less Idle' },
      { val: '100%', lbl: 'Compliance' },
    ],
  },
];

/* ── Minimal GPS Canvas — clean routes only, no dots ── */
function BgCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, raf, frame = 0;
    const resize = () => {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const routes = [
      { pts: [[0,0.78],[0.20,0.62],[0.42,0.48],[0.60,0.36],[0.80,0.24],[1.0,0.14]], col: '#D01B1B', w: 1.6 },
      { pts: [[0,0.42],[0.18,0.38],[0.36,0.52],[0.55,0.60],[0.74,0.52],[1.0,0.40]], col: '#A81515', w: 1.2 },
      { pts: [[0,0.90],[0.28,0.76],[0.48,0.66],[0.66,0.56],[0.85,0.44],[1.0,0.36]], col: '#CC2222', w: 1.2 },
      { pts: [[0,0.20],[0.22,0.17],[0.44,0.22],[0.66,0.16],[0.88,0.10],[1.0,0.06]], col: '#D01B1B', w: 0.9 },
    ];

    /* travelling dots */
    const travelers = routes.map((r, i) => ({ t: i * 0.22, r }));

    /* ping locations */
    const pings = [
      { x: 0.60, y: 0.36, ph: 0 },
      { x: 0.36, y: 0.52, ph: 2.0 },
      { x: 0.74, y: 0.52, ph: 4.0 },
    ];

    function cr(pts, t) {
      const n = pts.length - 1;
      const s = Math.min(Math.floor(t * n), n - 1);
      const lt = t * n - s;
      const p0 = pts[Math.max(s-1,0)], p1 = pts[s];
      const p2 = pts[Math.min(s+1,n)], p3 = pts[Math.min(s+2,n)];
      const t2 = lt*lt, t3 = t2*lt;
      return [
        0.5*((2*p1[0])+(-p0[0]+p2[0])*lt+(2*p0[0]-5*p1[0]+4*p2[0]-p3[0])*t2+(-p0[0]+3*p1[0]-3*p2[0]+p3[0])*t3),
        0.5*((2*p1[1])+(-p0[1]+p2[1])*lt+(2*p0[1]-5*p1[1]+4*p2[1]-p3[1])*t2+(-p0[1]+3*p1[1]-3*p2[1]+p3[1])*t3),
      ];
    }

    function rgba(hex, a) {
      const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
      return `rgba(${r},${g},${b},${a})`;
    }

    function draw() {
      frame++;
      ctx.clearRect(0, 0, W, H);

      /* grid */
      const gs = Math.round(W / 22);
      ctx.save();
      ctx.strokeStyle = 'rgba(208,27,27,0.055)';
      ctx.lineWidth = 1;
      for (let x = 0; x <= W; x += gs) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
      for (let y = 0; y <= H; y += gs) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }
      ctx.restore();

      /* route paths */
      routes.forEach(r => {
        ctx.save();
        ctx.setLineDash([10, 7]);
        ctx.lineWidth = r.w;
        ctx.strokeStyle = rgba(r.col, 0.22);
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';
        ctx.beginPath();
        for (let i = 0; i <= 100; i++) {
          const [px, py] = cr(r.pts, Math.min(i/100, 0.999));
          i === 0 ? ctx.moveTo(px*W, py*H) : ctx.lineTo(px*W, py*H);
        }
        ctx.stroke();
        ctx.restore();
      });

      /* travelling vehicle dots */
      travelers.forEach(v => {
        v.t += 0.0012; if (v.t > 1) v.t = 0;
        const [rx, ry] = cr(v.r.pts, Math.min(v.t, 0.999));
        const x = rx*W, y = ry*H, c = v.r.col;
        const g = ctx.createRadialGradient(x,y,0, x,y,16);
        g.addColorStop(0, rgba(c, 0.45)); g.addColorStop(1, rgba(c, 0));
        ctx.beginPath(); ctx.arc(x,y,16,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
        ctx.beginPath(); ctx.arc(x,y,4.5,0,Math.PI*2); ctx.fillStyle=c; ctx.fill();
        ctx.beginPath(); ctx.arc(x,y,2,0,Math.PI*2); ctx.fillStyle='rgba(255,255,255,0.9)'; ctx.fill();
      });

      /* ping rings */
      pings.forEach(p => {
        const px = p.x*W, py = p.y*H;
        const prog = ((frame * 0.022 + p.ph) % (Math.PI*2));
        const t1 = (Math.sin(prog)+1)/2;
        const t2 = (Math.sin(prog + 1.2)+1)/2;
        [[t1,0.5],[t2,0.22]].forEach(([pg,alpha]) => {
          ctx.beginPath();
          ctx.arc(px, py, 5 + pg*24, 0, Math.PI*2);
          ctx.strokeStyle = `rgba(208,27,27,${alpha*(1-pg)})`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        });
        ctx.beginPath(); ctx.arc(px,py,4.5,0,Math.PI*2); ctx.fillStyle='#D01B1B'; ctx.fill();
        ctx.beginPath(); ctx.arc(px,py,2,0,Math.PI*2); ctx.fillStyle='#fff'; ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} className="hero-canvas" />;
}

/* ── Hero ── */
const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef(null);
  const DURATION = 6000;

  const goTo = (idx) => {
    if (animating || idx === current) return;
    setAnimating(true);
    setTimeout(() => { setCurrent(idx); setAnimating(false); }, 380);
  };
  const next = () => goTo((current + 1) % slides.length);

  useEffect(() => {
    timerRef.current = setTimeout(next, DURATION);
    return () => { clearTimeout(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const slide = slides[current];

  return (
    <section className="hero" id="home">
      <div className="hero-bg" />

      <video
        className="hero-video"
        src="/dtsherovid.mp4"
        autoPlay
        muted
        loop
        playsInline
      />

      <BgCanvas />
      <div className="hero-overlay" />

      <div className="container hero-inner">
        <div className={`hero-content ${animating ? 'hero-exit' : 'hero-enter'}`}>

          <div className="hero-tag">
            <span className="tag-pulse" />
            {slide.tag}
          </div>

          <h1 className="hero-heading">
            {slide.headline}<br />
            <span className="hero-accent">{slide.accent}</span>
          </h1>

          <p className="hero-subtext">{slide.subtext}</p>

          <div className="hero-actions">
            <a href="/contact-us" className="hero-btn-primary">
              {slide.cta}
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </a>

          </div>

          <div className="hero-stats">
            {slide.stats.map((s, i) => (
              <div key={i} className="hero-stat">
                <div className="hstat-val">{s.val}</div>
                <div className="hstat-lbl">{s.lbl}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Hero;